import {
  presets,
  DEFAULT_BRAND_ID,
  reportColors,
  reportFills,
  domainColors,
  domainsWithWhiteText,
  handFont,
  statusColors,
} from "./presets.js";
import { dataPalettes, seriesHsl } from "./palettes.js";

function freeze(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
}

export {
  DEFAULT_BRAND_ID,
  reportColors,
  reportFills,
  domainColors,
  domainsWithWhiteText,
  handFont,
  statusColors,
  dataPalettes,
  seriesHsl,
};
export const brands = freeze(presets);

/** Resolve untrusted preferences without allowing prototype-property lookups. */
export function resolveBrand(id) {
  return typeof id === "string" && Object.hasOwn(brands, id)
    ? brands[id]
    : brands[DEFAULT_BRAND_ID];
}

/** Hex -> HSL channels for Tailwind's hsl(var(--token) / <alpha-value>) contract. */
export function hexToHsl(hex) {
  const [r, g, b] = hex
    .slice(1)
    .match(/../g)
    .map((n) => parseInt(n, 16) / 255);
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    delta = max - min;
  const lightness = (max + min) / 2;
  const saturation =
    delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;
  if (delta) {
    hue =
      max === r
        ? ((g - b) / delta) % 6
        : max === g
          ? (b - r) / delta + 2
          : (r - g) / delta + 4;
    hue = (hue * 60 + 360) % 360;
  }
  return `${+hue.toFixed(5)} ${+(saturation * 100).toFixed(5)}% ${+(lightness * 100).toFixed(5)}%`;
}

export function brandCssVariables(brand = resolveBrand()) {
  const vars = {};
  for (const [key, value] of Object.entries(brand.colors)) {
    const name = key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
    vars[`--cb-${name}`] = value;
    vars[`--cb-${name}-hsl`] = hexToHsl(value);
  }
  for (const [key, value] of Object.entries(reportColors)) {
    const name = key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
    vars[`--cb-report-${name}`] = value;
  }
  for (const [key, value] of Object.entries(reportFills))
    vars[`--cb-report-fill-${key}`] = value;
  for (const [role, steps] of Object.entries(domainColors))
    for (const [step, value] of Object.entries(steps)) {
      vars[`--cb-domain-${role}-${step}`] = value;
      vars[`--cb-domain-${role}-${step}-hsl`] = hexToHsl(value);
    }
  for (const [key, value] of Object.entries(brand.fonts))
    vars[`--cb-font-${key}`] = value;
  vars["--cb-font-hand"] = handFont;
  return vars;
}

/** Pure renderer, shared by the generated browser file and drift verification. */
export function renderBrandStylesheet() {
  const block = (selector, brand) =>
    `${selector} {\n${Object.entries(brandCssVariables(brand))
      .map(([key, value]) => `  ${key}: ${value};`)
      .join("\n")}\n}`;
  return (
    "/* Generated by @appcaire/brand. Edit presets.js, then yarn brand:generate. */\n" +
    block(":root", resolveBrand()) +
    "\n\n" +
    Object.values(brands)
      .map((brand) => block(`:root[data-caire-brand="${brand.id}"]`, brand))
      .join("\n\n") +
    "\n"
  );
}

/** Tailwind color with opacity support and a fallback for non-CAIRE consumers. */
export function tailwindBrandColor(key) {
  const name = key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
  return `hsl(var(--cb-${name}-hsl, ${hexToHsl(resolveBrand().colors[key])}) / <alpha-value>)`;
}

/** CSS paint for inline styles and SVG, including alpha without hex concatenation. */
export function brandColor(key, alpha = 1) {
  return tailwindBrandColor(key).replace("<alpha-value>", String(alpha));
}

/** Build-time HTML adapter. Never reads a preview preference or browser state. */
export function brandDocumentHead(html, surface, brand = resolveBrand()) {
  const icon =
    surface === "dashboard"
      ? brand.assets.dashboardIcon
      : brand.assets.websiteIcon;
  return html
    .replace(
      /(<meta\s+name="theme-color"\s+content=")[^"]*("\s*\/?>)/g,
      `$1${brand.colors.action}$2`,
    )
    .replace(
      /(<link\s+rel="icon"\s+[^>]*href=")[^"]+\.svg("[^>]*>)/,
      `$1${icon}$2`,
    );
}

export {
  CAIRE_WORDMARK,
  CAIRE_DESCRIPTORS,
  renderCaireWordmarkSvg,
} from "./wordmark.js";
